import { useState } from "react";
import { ChevronRight, ChevronDown, Folder, File } from "lucide-react";
import { cn } from "@/lib/utils";
import { useDirectory, type FileEntry } from "@/hooks/useProjectFiles";

interface FileTreeProps {
  root: string;
  onSelect: (path: string) => void;
  selectedPath: string | null;
}

export function FileTree({ root, onSelect, selectedPath }: FileTreeProps) {
  return (
    <div className="h-full overflow-auto py-1 text-sm">
      <DirLevel root={root} path={root} depth={0} onSelect={onSelect} selectedPath={selectedPath} />
    </div>
  );
}

function DirLevel({
  root, path, depth, onSelect, selectedPath,
}: FileTreeProps & { path: string; depth: number }) {
  const { entries, loading, error } = useDirectory(root, path);

  if (loading) {
    return <div className="px-2 py-1 text-xs text-muted-foreground" style={{ paddingLeft: depth * 12 + 8 }}>Loading...</div>;
  }
  if (error) {
    return <div className="px-2 py-1 text-xs text-muted-foreground" style={{ paddingLeft: depth * 12 + 8 }}>{error}</div>;
  }
  return (
    <ul>
      {entries.map((entry) => (
        <TreeNode
          key={entry.path}
          entry={entry}
          root={root}
          depth={depth}
          onSelect={onSelect}
          selectedPath={selectedPath}
        />
      ))}
    </ul>
  );
}

function TreeNode({
  entry, root, depth, onSelect, selectedPath,
}: { entry: FileEntry; root: string; depth: number; onSelect: (p: string) => void; selectedPath: string | null }) {
  const [open, setOpen] = useState(false);

  if (entry.isDir) {
    return (
      <li>
        <button
          type="button"
          className="flex w-full items-center gap-1 px-2 py-0.5 hover:bg-muted/50"
          style={{ paddingLeft: depth * 12 + 8 }}
          onClick={() => setOpen((o) => !o)}
        >
          {open ? <ChevronDown className="h-3 w-3" /> : <ChevronRight className="h-3 w-3" />}
          <Folder className="h-3.5 w-3.5 text-muted-foreground" />
          <span className="truncate">{entry.name}</span>
        </button>
        {open && (
          <DirLevel root={root} path={entry.path} depth={depth + 1} onSelect={onSelect} selectedPath={selectedPath} />
        )}
      </li>
    );
  }
  return (
    <li>
      <button
        type="button"
        className={cn(
          "flex w-full items-center gap-1 px-2 py-0.5 hover:bg-muted/50",
          selectedPath === entry.path && "bg-muted",
        )}
        style={{ paddingLeft: depth * 12 + 24 }}
        onClick={() => onSelect(entry.path)}
      >
        <File className="h-3.5 w-3.5 text-muted-foreground" />
        <span className="truncate">{entry.name}</span>
      </button>
    </li>
  );
}
